import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { getPlayerStats, getGameHistory } from '@/utils/statsService';
import { 
  Trophy, Target, Flame, TrendingUp, Clock, 
  Crown, Swords, BarChart3, X, ChevronRight,
  Medal, Zap, Calendar
} from 'lucide-react';

const StatCard = ({ icon: Icon, label, value, subtext, color, theme }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`
      p-4 rounded-xl
      ${theme === 'cosmic'
        ? 'bg-slate-800/60 border border-slate-700'
        : 'bg-white border border-gray-200 shadow-sm'
      }
    `}
  >
    <div className="flex items-start justify-between">
      <div className={`p-2 rounded-lg ${color}`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <span className={`text-2xl font-bold ${theme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
        {value}
      </span>
    </div>
    <p className={`mt-2 text-sm font-medium ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
      {label}
    </p>
    {subtext && (
      <p className={`text-xs ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
        {subtext}
      </p>
    )}
  </motion.div>
);

const WinRateRing = ({ winRate, theme }) => {
  const radius = 45;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (winRate / 100) * circumference;
  
  return (
    <div className="relative w-32 h-32 mx-auto">
      <svg className="w-32 h-32 transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="64"
          cy="64"
          r={radius}
          stroke={theme === 'cosmic' ? '#1e293b' : '#e5e7eb'}
          strokeWidth="10"
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx="64"
          cy="64"
          r={radius}
          stroke={winRate >= 50 ? '#22c55e' : '#ef4444'}
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-3xl font-bold ${theme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
          {winRate}%
        </span>
        <span className={`text-xs ${theme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}`}>
          Win Rate
        </span>
      </div>
    </div>
  );
};

const GameHistoryItem = ({ game, theme }) => {
  const resultColors = {
    win: 'bg-green-500',
    loss: 'bg-red-500',
    draw: 'bg-yellow-500'
  };
  
  const resultIcons = {
    win: Trophy,
    loss: Swords,
    draw: Target
  };
  
  const Icon = resultIcons[game.result] || Target;
  const date = new Date(game.played_at).toLocaleDateString();
  
  return (
    <div className={`
      flex items-center gap-3 p-3 rounded-lg
      ${theme === 'cosmic' ? 'bg-slate-800/50' : 'bg-gray-50'}
    `}>
      <div className={`w-8 h-8 rounded-full ${resultColors[game.result]} flex items-center justify-center`}>
        <Icon className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className={`font-medium capitalize ${theme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
            {game.result}
          </span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${
            theme === 'cosmic' ? 'bg-slate-700 text-gray-300' : 'bg-gray-200 text-gray-600'
          }`}>
            {game.difficulty}
          </span>
        </div>
        <div className={`text-xs ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
          {game.total_moves} moves • {game.player_color} • {date}
        </div>
      </div>
    </div>
  );
};

const DifficultyBar = ({ difficulty, data, theme }) => {
  const winRate = data.total > 0 ? Math.round((data.wins / data.total) * 100) : 0;
  
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className={`capitalize ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
          {difficulty}
        </span>
        <span className={theme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}>
          {data.wins}/{data.total} ({winRate}%)
        </span>
      </div>
      <div className={`h-2 rounded-full overflow-hidden ${theme === 'cosmic' ? 'bg-slate-700' : 'bg-gray-200'}`}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${winRate}%` }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className={`h-full ${winRate >= 50 ? 'bg-green-500' : 'bg-orange-500'}`}
        />
      </div>
    </div>
  );
};

const StatsDialog = ({ isOpen, onClose, theme }) => {
  const { isAuthenticated } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (isOpen && isAuthenticated) {
      loadStats();
    }
  }, [isOpen, isAuthenticated]);

  const loadStats = async () => {
    setLoading(true);
    const [statsData, historyData] = await Promise.all([
      getPlayerStats(),
      getGameHistory(10)
    ]);
    setStats(statsData);
    setHistory(historyData || []);
    setLoading(false);
  };

  if (!isAuthenticated) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`
          max-w-md
          ${theme === 'cosmic'
            ? 'bg-slate-900 border-slate-700 text-white'
            : 'bg-white'
          }
        `}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Game Statistics
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-8">
            <Trophy className={`w-16 h-16 mx-auto mb-4 ${theme === 'cosmic' ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={theme === 'cosmic' ? 'text-gray-400' : 'text-gray-500'}>
              Sign in to track your progress and view statistics
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={`
        max-w-2xl max-h-[85vh] overflow-hidden
        ${theme === 'cosmic'
          ? 'bg-slate-900 border-slate-700 text-white'
          : 'bg-white'
        }
      `}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className={theme === 'cosmic' ? 'text-cyan-400' : 'text-amber-600'} />
            Your Statistics
          </DialogTitle>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-700 pb-2">
          {['overview', 'history'].map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-colors capitalize
                ${activeTab === tab
                  ? theme === 'cosmic'
                    ? 'bg-cyan-500/20 text-cyan-400'
                    : 'bg-amber-100 text-amber-700'
                  : theme === 'cosmic'
                    ? 'text-gray-400 hover:text-gray-300'
                    : 'text-gray-500 hover:text-gray-700'
                }
              `}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="overflow-y-auto max-h-[60vh] pr-2">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : !stats || stats.total_games === 0 ? (
            <div className="text-center py-12">
              <Swords className={`w-16 h-16 mx-auto mb-4 ${theme === 'cosmic' ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-lg font-medium ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
                No games played yet
              </p>
              <p className={`text-sm ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                Complete a game to start tracking your progress
              </p>
            </div>
          ) : activeTab === 'overview' ? (
            <div className="space-y-6 py-4">
              {/* Win Rate Ring */}
              <div className="text-center">
                <WinRateRing winRate={stats.win_rate} theme={theme} />
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                  icon={Trophy}
                  label="Wins"
                  value={stats.wins}
                  color="bg-green-500"
                  theme={theme}
                />
                <StatCard
                  icon={Swords}
                  label="Losses"
                  value={stats.losses}
                  color="bg-red-500"
                  theme={theme}
                />
                <StatCard
                  icon={Target}
                  label="Draws"
                  value={stats.draws}
                  color="bg-yellow-500"
                  theme={theme}
                />
                <StatCard
                  icon={BarChart3}
                  label="Total"
                  value={stats.total_games}
                  color="bg-blue-500"
                  theme={theme}
                />
              </div>

              {/* Streaks */}
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  icon={Flame}
                  label="Current Streak"
                  value={stats.current_streak}
                  subtext="consecutive wins"
                  color="bg-orange-500"
                  theme={theme}
                />
                <StatCard
                  icon={Medal}
                  label="Best Streak"
                  value={stats.best_streak}
                  subtext="all-time record"
                  color="bg-purple-500"
                  theme={theme}
                />
              </div>

              {/* Win Rate by Difficulty */}
              {stats.games_by_difficulty && Object.keys(stats.games_by_difficulty).length > 0 && (
                <div className={`
                  p-4 rounded-xl
                  ${theme === 'cosmic' ? 'bg-slate-800/60 border border-slate-700' : 'bg-gray-50'}
                `}>
                  <h3 className={`font-medium mb-3 flex items-center gap-2 ${theme === 'cosmic' ? 'text-gray-200' : 'text-gray-800'}`}>
                    <TrendingUp className="w-4 h-4" />
                    Performance by Difficulty
                  </h3>
                  <div className="space-y-3">
                    {['easy', 'medium', 'hard', 'expert'].map((diff) => 
                      stats.games_by_difficulty[diff] && (
                        <DifficultyBar 
                          key={diff}
                          difficulty={diff}
                          data={stats.games_by_difficulty[diff]}
                          theme={theme}
                        />
                      )
                    )}
                  </div>
                </div>
              )}

              {/* Extra Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className={`
                  p-4 rounded-xl
                  ${theme === 'cosmic' ? 'bg-slate-800/60 border border-slate-700' : 'bg-gray-50'}
                `}>
                  <div className="flex items-center gap-2 mb-2">
                    <Crown className={`w-4 h-4 ${theme === 'cosmic' ? 'text-cyan-400' : 'text-amber-600'}`} />
                    <span className={`text-sm ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Color Preference
                    </span>
                  </div>
                  <div className="flex gap-4">
                    <div>
                      <span className={`text-lg font-bold ${theme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
                        {stats.games_as_white}
                      </span>
                      <span className={`text-xs ml-1 ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                        white
                      </span>
                    </div>
                    <div>
                      <span className={`text-lg font-bold ${theme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
                        {stats.games_as_black}
                      </span>
                      <span className={`text-xs ml-1 ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                        black
                      </span>
                    </div>
                  </div>
                </div>
                <div className={`
                  p-4 rounded-xl
                  ${theme === 'cosmic' ? 'bg-slate-800/60 border border-slate-700' : 'bg-gray-50'}
                `}>
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className={`w-4 h-4 ${theme === 'cosmic' ? 'text-cyan-400' : 'text-amber-600'}`} />
                    <span className={`text-sm ${theme === 'cosmic' ? 'text-gray-300' : 'text-gray-700'}`}>
                      Avg. Game Length
                    </span>
                  </div>
                  <span className={`text-lg font-bold ${theme === 'cosmic' ? 'text-white' : 'text-gray-900'}`}>
                    {stats.avg_game_length} moves
                  </span>
                </div>
              </div>
            </div>
          ) : (
            /* History Tab */
            <div className="space-y-2 py-4">
              {history.length === 0 ? (
                <p className={`text-center py-8 ${theme === 'cosmic' ? 'text-gray-500' : 'text-gray-500'}`}>
                  No game history yet
                </p>
              ) : (
                history.map((game, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                  >
                    <GameHistoryItem game={game} theme={theme} />
                  </motion.div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Close button */}
        <div className="flex justify-end pt-2">
          <Button
            variant="outline"
            onClick={onClose}
            className={theme === 'cosmic' ? 'border-slate-600' : ''}
          >
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatsDialog;
